import { useState, useMemo } from 'react';
import { format, addDays, startOfToday } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import {
  MessageSquare, UtensilsCrossed, Star, ChefHat, Sparkles, Globe,
  ChevronLeft, ChevronRight, Users, Calendar, Clock, User, Gift, Search,
  Heart, Cake, GraduationCap, Briefcase, Gem, Home, Ban, Users2,
  AlertTriangle, Accessibility, Baby, Check, Send,
} from 'lucide-react';

const OCCASIONS = [
  { id:'anniversary', label:'Aniversario',     Icon:Heart },
  { id:'romantic',    label:'Cita romántica',  Icon:Heart },
  { id:'birthday',    label:'Cumpleaños',      Icon:Cake },
  { id:'friends',     label:'Entre amigos',    Icon:Users2 },
  { id:'graduation',  label:'Graduación',      Icon:GraduationCap },
  { id:'business',    label:'Negocios',        Icon:Briefcase },
  { id:'proposal',    label:'Propuesta',       Icon:Gem },
  { id:'family',      label:'Reunión familiar', Icon:Home },
  { id:'none',        label:'No la hay',       Icon:Ban },
];
const SPECIAL_REQS = [
  { id:'accessibility', label:'Apoyo especial', Icon:Accessibility },
  { id:'baby_seat',     label:'Silla de bebé',  Icon:Baby },
];
const QUICK_TIMES = ['12:00','12:30','13:00','13:30','14:00','14:30','15:00','19:00','19:30','20:00','20:30','21:00','21:30','22:00','22:30'];

export const LANGUAGES = [
  { code:'es', flag:'🇲🇽', name:'Español' },
  { code:'en', flag:'🇺🇸', name:'English' },
  { code:'fr', flag:'🇫🇷', name:'Français' },
  { code:'it', flag:'🇮🇹', name:'Italiano' },
  { code:'pt', flag:'🇧🇷', name:'Português' },
  { code:'de', flag:'🇩🇪', name:'Deutsch' },
  { code:'zh', flag:'🇨🇳', name:'中文' },
  { code:'ko', flag:'🇰🇷', name:'한국어' },
  { code:'ja', flag:'🇯🇵', name:'日本語' },
  { code:'ar', flag:'🇸🇦', name:'العربية' },
];

export const WIDGET_TRANSLATIONS = {
  es:{ title:'Reservación', step4:'Confirma tu reservación', next:'Siguiente', confirm:'Confirmar Reservación', pay:'Confirmar y pagar', payAtRest:'Pagar en el restaurante', payOnline:'Pagar en línea', payWallet:'Pagar con cartera', paymentMethod:'Método de pago', yourOrder:'Tu pedido', estimatedTotal:'Total estimado' },
  en:{ title:'Reservation', step4:'Confirm your reservation', next:'Next', confirm:'Confirm Reservation', pay:'Confirm & pay', payAtRest:'Pay at restaurant', payOnline:'Pay online', payWallet:'Pay from wallet', paymentMethod:'Payment method', yourOrder:'Your order', estimatedTotal:'Estimated total' },
  fr:{ title:'Réservation', step4:'Confirmer', next:'Suivant', confirm:'Confirmer', pay:'Confirmer et payer', payAtRest:'Payer au restaurant', payOnline:'Payer en ligne', payWallet:'Payer par portefeuille', paymentMethod:'Mode de paiement', yourOrder:'Votre commande', estimatedTotal:'Total estimé' },
  it:{ title:'Prenotazione', step4:'Conferma', next:'Avanti', confirm:'Conferma prenotazione', pay:'Conferma e paga', payAtRest:'Paga al ristorante', payOnline:'Paga online', payWallet:'Paga con portafoglio', paymentMethod:'Metodo di pagamento', yourOrder:'Il tuo ordine', estimatedTotal:'Totale stimato' },
  pt:{ title:'Reserva', step4:'Confirmar reserva', next:'Próximo', confirm:'Confirmar reserva', pay:'Confirmar e pagar', payAtRest:'Pagar no restaurante', payOnline:'Pagar online', payWallet:'Pagar com carteira', paymentMethod:'Forma de pagamento', yourOrder:'Seu pedido', estimatedTotal:'Total estimado' },
  de:{ title:'Reservierung', step4:'Bestätigen', next:'Weiter', confirm:'Reservierung bestätigen', pay:'Bestätigen & zahlen', payAtRest:'Im Restaurant zahlen', payOnline:'Online zahlen', payWallet:'Aus Geldbörse zahlen', paymentMethod:'Zahlungsmethode', yourOrder:'Ihre Bestellung', estimatedTotal:'Geschätzter Gesamtbetrag' },
  zh:{ title:'预约', step4:'确认预约', next:'下一步', confirm:'确认预约', pay:'确认并支付', payAtRest:'到餐厅付款', payOnline:'在线支付', payWallet:'钱包支付', paymentMethod:'支付方式', yourOrder:'您的订单', estimatedTotal:'预计总计' },
  ko:{ title:'예약', step4:'예약 확인', next:'다음', confirm:'예약 확인', pay:'확인 및 결제', payAtRest:'레스토랑에서 결제', payOnline:'온라인 결제', payWallet:'지갑으로 결제', paymentMethod:'결제 방법', yourOrder:'내 주문', estimatedTotal:'예상 합계' },
  ja:{ title:'予約', step4:'予約を確認', next:'次へ', confirm:'予約を確定', pay:'確定して支払う', payAtRest:'レストランで支払う', payOnline:'オンラインで支払う', payWallet:'財布で支払う', paymentMethod:'お支払い方法', yourOrder:'ご注文', estimatedTotal:'合計金額' },
  ar:{ title:'حجز', step4:'تأكيد الحجز', next:'التالي', confirm:'تأكيد الحجز', pay:'تأكيد والدفع', payAtRest:'الدفع في المطعم', payOnline:'الدفع عبر الإنترنت', payWallet:'الدفع بالمحفظة', paymentMethod:'طريقة الدفع', yourOrder:'طلبك', estimatedTotal:'الإجمالي المتوقع' },
};

const TODAY = startOfToday();

/**
 * Shared interactive Carta Express Widget.
 * Used both in admin preview (WidgetCartaExpress) and public embed (CartaWidgetPublicPage).
 */
export default function CartaExpressWidget({ config, menus, onConfirm }) {
  const pc = config.primaryColor || '#F97316';
  const [lang, setLang] = useState(config.language || 'es');
  const [showLangMenu, setShowLangMenu] = useState(false);
  const t = WIDGET_TRANSLATIONS[lang] || WIDGET_TRANSLATIONS.es;
  const isRtl = lang === 'ar';
  const payMethods = config.paymentMethods || ['restaurant'];
  const [paymentSel, setPaymentSel] = useState(payMethods[0] || 'restaurant');
  const quickDates = useMemo(() => Array.from({ length: 5 }, (_, i) => addDays(TODAY, i)), []);

  const [step, setStep] = useState(1);
  const [resType, setResType] = useState('self');
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestWhatsapp, setGuestWhatsapp] = useState('');
  const [nickname, setNickname] = useState('');
  const [occasion, setOccasion] = useState(null);
  const [customOccasion, setCustomOccasion] = useState('');
  const [allergies, setAllergies] = useState('');
  const [requests, setRequests] = useState('');
  const [requirements, setRequirements] = useState([]);
  const [customReq, setCustomReq] = useState('');
  const [partySize, setPartySize] = useState(2);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [showCal, setShowCal] = useState(false);
  const [calMonth, setCalMonth] = useState(new Date());
  const [menuId, setMenuId] = useState(null);
  const [catFilter, setCatFilter] = useState('all');
  const [order, setOrder] = useState([]);
  const [detailDish, setDetailDish] = useState(null);
  const [dishQty, setDishQty] = useState({});
  const [confirmed, setConfirmed] = useState(false);

  const safeMenus = Array.isArray(menus) ? menus : [];
  const activeMenu = safeMenus.find(m => m.id === (menuId || safeMenus[0]?.id)) || safeMenus[0];
  const dishes = Array.isArray(activeMenu?.dishes) ? activeMenu.dishes : [];
  const cats = [...new Set(dishes.map(d => d.category).filter(Boolean))];
  const filteredDishes = dishes.filter(d => catFilter === 'all' || d.category === catFilter);
  const totalItems = order.reduce((s,i) => s+i.qty, 0);
  const totalPrice = order.reduce((s,i) => s+(i.price||0)*i.qty, 0);

  const toggleReq = (id) => setRequirements(p => p.includes(id) ? p.filter(r=>r!==id) : [...p,id]);

  const addToOrder = (dish, qty) => {
    setOrder(p => {
      const ex = p.find(i=>i.dish_id===dish.id);
      if (ex) return p.map(i=>i.dish_id===dish.id ? {...i, qty:i.qty+qty} : i);
      return [...p, { dish_id:dish.id, name:dish.name, price:dish.price, qty }];
    });
    setDetailDish(null);
    setDishQty(p=>({...p,[dish.id]:0}));
    toast.success(`${dish.name} agregado`);
  };

  const removeFromOrder = (dishId) => { setOrder(p=>p.filter(i=>i.dish_id!==dishId)); };

  const renderCal = () => {
    const dim = new Date(calMonth.getFullYear(), calMonth.getMonth()+1, 0).getDate();
    const first = new Date(calMonth.getFullYear(), calMonth.getMonth(), 1).getDay();
    const days = [];
    for (let i=0;i<first;i++) days.push(<div key={`e${i}`} />);
    for (let d=1;d<=dim;d++) {
      const date = new Date(calMonth.getFullYear(), calMonth.getMonth(), d);
      const isPast = date < TODAY;
      const isSel = selectedDate && format(date,'yyyy-MM-dd')===format(selectedDate,'yyyy-MM-dd');
      const isToday = format(date,'yyyy-MM-dd')===format(TODAY,'yyyy-MM-dd');
      days.push(
        <button key={d} disabled={isPast} onClick={() => { if(!isPast){setSelectedDate(date);} }}
          className={cn('w-full aspect-square flex items-center justify-center text-xl font-bold rounded-2xl transition-all',
            isPast ? 'text-gray-300 cursor-not-allowed' : isSel ? 'text-white shadow-lg scale-105' : isToday ? 'bg-orange-100 text-orange-600 hover:bg-orange-200' : 'hover:bg-gray-100 text-gray-700')}
          style={isSel ? { backgroundColor:pc, boxShadow:`0 4px 12px ${pc}40` } : {}}
        >{d}</button>
      );
    }
    return days;
  };

  const canGo1 = resType === 'external' ? (guestName.trim() !== '') : nickname.trim() !== '';

  const hdrStyle = { background:`linear-gradient(135deg,${pc},${pc}dd)`, padding:'24px 24px 16px', textAlign:'center' };
  const bodyStyle = { padding:24, maxHeight:'60vh', overflowY:'auto' };
  const footStyle = { padding:'16px 24px', borderTop:'1px solid #f3f4f6', background:'#f9fafb' };
  const inputSt = { width:'100%', padding:'10px 14px', border:'1px solid #e5e7eb', borderRadius:10, fontSize:14, outline:'none', boxSizing:'border-box', fontFamily:'inherit' };
  const sectionH = (Icon, txt, sub) => (
    <div style={{ marginBottom:12 }}>
      <h3 style={{ fontWeight:700, fontSize:15, margin:0, display:'flex', alignItems:'center', gap:6 }}>
        <Icon size={18} style={{ color:pc }} /> {txt}
      </h3>
      {sub && <p style={{ fontSize:13, color:'#9ca3af', margin:'4px 0 0' }}>{sub}</p>}
    </div>
  );

  const [submitting, setSubmitting] = useState(false);
  const handleConfirm = async () => {
    if (typeof onConfirm !== 'function') { setConfirmed(true); return; }
    setSubmitting(true);
    try {
      await onConfirm({
        guestName: resType==='external' ? guestName : nickname,
        guestEmail, guestWhatsapp, partySize, selectedDate, selectedTime,
        occasion, customOccasion, allergies, requests, requirements, customReq,
        paymentMethod: paymentSel, order, totalPrice,
      });
      setConfirmed(true);
    } catch (err) {
      toast.error(err?.message || 'No pudimos enviar la reservación');
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <div className="w-full max-w-md mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div style={hdrStyle}>
          {config.restaurantLogo
            ? <img src={config.restaurantLogo} alt="" className="w-32 h-32 mx-auto object-contain rounded-2xl bg-white/10 p-2" />
            : <div className="w-32 h-32 mx-auto bg-white/20 rounded-2xl flex items-center justify-center"><ChefHat className="w-16 h-16 text-white" /></div>}
          <h1 className="text-xl font-bold text-white mt-3">{config.restaurantName || 'Mi Restaurante'}</h1>
          <div className="flex items-center justify-center gap-2 mt-4">{[1,2,3,4].map(s=><div key={s} style={{ height:8, width:s<=4?16:8, borderRadius:4, background:'rgba(255,255,255,0.7)' }} />)}</div>
        </div>
        <div style={{ padding:32, textAlign:'center' }}>
          <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4" style={{ background:`${pc}20` }}><Check size={36} style={{ color:pc }} /></div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">¡Reserva confirmada!</h2>
          <p className="text-gray-500 text-sm mb-6">Recibirás un correo con los detalles.</p>
          <div className="text-left space-y-2 mb-6 p-4 bg-gray-50 rounded-xl text-sm text-gray-700">
            <div><strong>Nombre:</strong> {resType==='external' ? guestName : nickname || 'Usuario'}</div>
            {selectedDate && <div><strong>Fecha:</strong> {format(selectedDate,"d 'de' MMMM yyyy",{locale:es})}</div>}
            <div><strong>Hora:</strong> {selectedTime}</div>
            <div><strong>Personas:</strong> {partySize}</div>
            {occasion && <div><strong>Ocasión:</strong> {OCCASIONS.find(o=>o.id===occasion)?.label}</div>}
            {order.length>0 && <div><strong>Pedido anticipado:</strong> {totalItems} platillos · ${totalPrice.toFixed(2)}</div>}
          </div>
          <button onClick={() => { setStep(1); setConfirmed(false); setOrder([]); setNickname(''); setGuestName(''); setOccasion(null); setSelectedDate(null); setSelectedTime(null); }} style={{ width:'100%', padding:'14px', borderRadius:12, border:'none', background:pc, color:'#fff', fontWeight:700, fontSize:15, cursor:'pointer' }}>
            Nueva reserva
          </button>
        </div>
        <div style={footStyle}><p className="text-xs text-gray-400 text-center">Powered by <span className="font-semibold text-orange-500">OkReserva</span></p></div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden" style={{ position:'relative', direction: isRtl ? 'rtl' : 'ltr' }}>
      <div style={{ ...hdrStyle, position:'relative' }}>
        <div style={{ position:'absolute', top:12, right:12, zIndex:10 }}>
          <button
            onClick={() => setShowLangMenu(o => !o)}
            style={{ display:'flex', alignItems:'center', gap:4, padding:'4px 10px', borderRadius:20, background:'rgba(255,255,255,0.2)', border:'1px solid rgba(255,255,255,0.3)', color:'#fff', fontSize:12, fontWeight:600, cursor:'pointer' }}
          >
            <Globe size={12} />
            {LANGUAGES.find(l => l.code === lang)?.flag} {lang.toUpperCase()}
          </button>
          {showLangMenu && (
            <div style={{ position:'absolute', top:'110%', right:0, background:'#fff', borderRadius:12, boxShadow:'0 8px 24px rgba(0,0,0,0.18)', minWidth:160, overflow:'hidden', border:'1px solid #e5e7eb', zIndex:50 }}>
              {LANGUAGES.map(l => (
                <button key={l.code} onClick={() => { setLang(l.code); setShowLangMenu(false); }}
                  style={{ display:'flex', alignItems:'center', gap:8, width:'100%', padding:'8px 14px', background:lang===l.code?`${pc}10`:'transparent', border:'none', cursor:'pointer', fontSize:13, fontWeight:lang===l.code?700:400, color:lang===l.code?pc:'#374151', textAlign:'left' }}>
                  <span>{l.flag}</span> {l.name}
                </button>
              ))}
            </div>
          )}
        </div>
        {config.restaurantLogo
          ? <img src={config.restaurantLogo} alt="" className="w-32 h-32 mx-auto object-contain rounded-2xl bg-white/10 p-2" />
          : <div className="w-32 h-32 mx-auto bg-white/20 rounded-2xl flex items-center justify-center"><ChefHat className="w-16 h-16 text-white" /></div>}
        <h1 className="text-xl font-bold text-white mt-3">{config.restaurantName || 'Mi Restaurante'}</h1>
        <div className="flex items-center justify-center gap-2 mt-4">
          {[1,2,3,4].map(s => (
            <div key={s} className="h-2 rounded-full transition-all" style={{ width: s===step ? 32 : 16, background: s===step ? '#fff' : s<step ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.3)' }} />
          ))}
        </div>
      </div>

      <div style={bodyStyle}>
        {step===1 && (
          <div style={{ display:'flex', flexDirection:'column', gap:24 }}>
            <div>
              {sectionH(User, '¿A nombre de quién la reserva?')}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:12 }}>
                {[
                  { val:'self', label:'Para ti', Icon:User },
                  { val:'okuser', label:'Alguien más', Icon:Gift },
                  { val:'external', label:'Fuera de OkGo', Icon:Users },
                ].map(opt => (
                  <button key={opt.val} onClick={() => setResType(opt.val)} style={{
                    padding:'10px 6px', borderRadius:10, border:`2px solid ${resType===opt.val ? pc : '#e5e7eb'}`,
                    background: resType===opt.val ? `${pc}10` : 'transparent', cursor:'pointer', textAlign:'center', transition:'all 0.15s',
                  }}>
                    <opt.Icon size={18} style={{ color: resType===opt.val ? pc : '#9ca3af', margin:'0 auto 4px' }} />
                    <span style={{ fontSize:11, fontWeight:600 }}>{opt.label}</span>
                  </button>
                ))}
              </div>
              {resType==='external' ? (
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  <input style={inputSt} placeholder="Nombre completo" value={guestName} onChange={e=>setGuestName(e.target.value)} />
                  <input style={inputSt} type="email" placeholder="Correo electrónico" value={guestEmail} onChange={e=>setGuestEmail(e.target.value)} />
                  <input style={inputSt} type="tel" placeholder="WhatsApp" value={guestWhatsapp} onChange={e=>setGuestWhatsapp(e.target.value)} />
                </div>
              ) : (
                <div style={{ position:'relative' }}>
                  <input style={{ ...inputSt, paddingLeft:36 }} placeholder="OkUser - Tu nickname" value={nickname} onChange={e=>setNickname(e.target.value)} />
                  <Search size={16} style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'#9ca3af' }} />
                </div>
              )}
            </div>

            <div>
              {sectionH(Sparkles, '¿Cuál es la ocasión?', '¿Celebras algo en especial?')}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8, marginBottom:10 }}>
                {OCCASIONS.map(o => {
                  const sel = occasion===o.id;
                  return (
                    <button key={o.id} onClick={() => setOccasion(o.id)} style={{
                      padding:'10px 6px', borderRadius:10, border:`2px solid ${sel ? pc : '#e5e7eb'}`,
                      background: sel ? `${pc}10` : 'transparent', cursor:'pointer', textAlign:'center', transition:'all 0.15s',
                    }}>
                      <o.Icon size={18} style={{ color: sel ? pc : '#9ca3af', margin:'0 auto 4px' }} />
                      <span style={{ fontSize:10, fontWeight:600 }}>{o.label}</span>
                    </button>
                  );
                })}
              </div>
              <input style={inputSt} placeholder="¿Otro motivo?" value={customOccasion} onChange={e=>setCustomOccasion(e.target.value)} />
            </div>

            <div>
              {sectionH(AlertTriangle, '¿Alguna restricción alérgica?', 'Tuya o de tus acompañantes')}
              <textarea style={{ ...inputSt, resize:'none', minHeight:64 }} placeholder="Comparte los alérgenos." value={allergies} onChange={e=>setAllergies(e.target.value)} rows={2} />
            </div>

            <div>
              {sectionH(MessageSquare, '¿Alguna petición especial?')}
              <textarea style={{ ...inputSt, resize:'none', minHeight:64 }} placeholder="Comentarios o peticiones" value={requests} onChange={e=>setRequests(e.target.value)} rows={2} />
            </div>

            <div>
              {sectionH(Accessibility, '¿Algún requerimiento especial?')}
              <div style={{ display:'flex', gap:8, marginBottom:10 }}>
                {SPECIAL_REQS.map(r => {
                  const sel = requirements.includes(r.id);
                  return (
                    <button key={r.id} onClick={() => toggleReq(r.id)} style={{
                      flex:1, padding:'10px 8px', borderRadius:10, border:`2px solid ${sel ? pc : '#e5e7eb'}`,
                      background: sel ? `${pc}10` : 'transparent', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6, transition:'all 0.15s',
                    }}>
                      <r.Icon size={16} style={{ color: sel ? pc : '#9ca3af' }} />
                      <span style={{ fontSize:12, fontWeight:600 }}>{r.label}</span>
                    </button>
                  );
                })}
              </div>
              <input style={inputSt} placeholder="Agrega tu requerimiento" value={customReq} onChange={e=>setCustomReq(e.target.value)} />
            </div>
          </div>
        )}

        {step===2 && (
          <div style={{ display:'flex', flexDirection:'column', gap:24 }}>
            <div>
              {sectionH(Users, '¿Cuántas personas?')}
              <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:16 }}>
                <button onClick={() => setPartySize(Math.max(1,partySize-1))} style={{ width:48, height:48, borderRadius:'50%', border:'2px solid #e5e7eb', background:'transparent', cursor:'pointer', fontSize:22, fontWeight:700, color:'#6b7280' }}>−</button>
                <span style={{ fontSize:40, fontWeight:800, minWidth:64, textAlign:'center', color:pc }}>{partySize}</span>
                <button onClick={() => setPartySize(Math.min(20,partySize+1))} style={{ width:48, height:48, borderRadius:'50%', border:'2px solid #e5e7eb', background:'transparent', cursor:'pointer', fontSize:22, fontWeight:700, color:'#6b7280' }}>+</button>
              </div>
            </div>
            <div>
              {sectionH(Calendar, '¿Qué día?')}
              <div style={{ display:'flex', gap:8, overflowX:'auto', paddingBottom:8, marginBottom:8 }}>
                {quickDates.map(date => {
                  const sel = selectedDate && format(date,'yyyy-MM-dd')===format(selectedDate,'yyyy-MM-dd');
                  return (
                    <button key={date.toISOString()} onClick={()=>setSelectedDate(date)} style={{
                      flexShrink:0, minWidth:72, padding:'10px 8px', borderRadius:12, border:`2px solid ${sel ? pc : '#e5e7eb'}`,
                      background: sel ? `${pc}10` : 'transparent', cursor:'pointer', textAlign:'center', transition:'all 0.15s',
                      color: sel ? pc : '#374151',
                    }}>
                      <p style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', margin:0 }}>{format(date,'EEE',{locale:es})}</p>
                      <p style={{ fontSize:18, fontWeight:800, margin:'2px 0' }}>{format(date,'d')}</p>
                    </button>
                  );
                })}
                <button onClick={()=>setShowCal(true)} style={{ flexShrink:0, minWidth:72, padding:'10px 8px', borderRadius:12, border:'2px solid #e5e7eb', background:'transparent', cursor:'pointer', textAlign:'center' }}>
                  <Calendar size={18} style={{ margin:'0 auto 4px', color:'#9ca3af' }} />
                  <p style={{ fontSize:11, fontWeight:600, margin:0, color:'#9ca3af' }}>Más</p>
                </button>
              </div>
              {selectedDate && <p style={{ fontSize:13, fontWeight:600, color:pc, textAlign:'center', margin:'4px 0 0', textTransform:'capitalize' }}>{format(selectedDate,"EEEE d 'de' MMMM",{locale:es})}</p>}
            </div>
            {selectedDate && (
              <div>
                {sectionH(Clock, '¿A qué hora?')}
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8 }}>
                  {QUICK_TIMES.map(tm => {
                    const sel = selectedTime===tm;
                    const [h,m] = tm.split(':');
                    const hr = parseInt(h);
                    const ap = hr>=12 ? 'PM' : 'AM';
                    const h12 = hr%12||12;
                    return (
                      <button key={tm} onClick={()=>setSelectedTime(tm)} style={{
                        padding:'10px 4px', borderRadius:10, border:`2px solid ${sel ? pc : '#e5e7eb'}`,
                        background: sel ? `${pc}10` : 'transparent', cursor:'pointer', textAlign:'center', transition:'all 0.15s',
                        fontSize:12, fontWeight:600, color: sel ? pc : '#374151',
                      }}>{h12}:{m} {ap}</button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {step===3 && (
          <div>
            <div style={{ marginBottom:14 }}>
              <h3 style={{ fontWeight:700, fontSize:15, margin:'0 0 4px', display:'flex', alignItems:'center', gap:6 }}>
                <UtensilsCrossed size={18} style={{ color:pc }} /> Elige tus platillos
              </h3>
              <p style={{ fontSize:13, color:'#9ca3af', margin:0 }}>Pedido anticipado — listo a tu llegada</p>
            </div>

            {safeMenus.length > 1 && (
              <div style={{ display:'flex', gap:6, overflowX:'auto', paddingBottom:8, marginBottom:10 }}>
                {safeMenus.map(m => {
                  const act = (menuId||safeMenus[0]?.id)===m.id;
                  return (
                    <button key={m.id} onClick={()=>{setMenuId(m.id);setCatFilter('all');}} style={{
                      flexShrink:0, padding:'6px 14px', borderRadius:20, border:`2px solid ${act ? pc : '#e5e7eb'}`,
                      background: act ? pc : 'transparent', color: act ? '#fff' : '#6b7280', fontSize:12, fontWeight:600, cursor:'pointer',
                    }}>{m.name}</button>
                  );
                })}
              </div>
            )}

            {cats.length > 0 && (
              <div style={{ display:'flex', gap:6, overflowX:'auto', paddingBottom:8, marginBottom:10 }}>
                {['all',...cats].map(c => (
                  <button key={c} onClick={()=>setCatFilter(c)} style={{
                    flexShrink:0, padding:'4px 12px', borderRadius:20, border:`1px solid ${catFilter===c ? pc : '#e5e7eb'}`,
                    background: catFilter===c ? `${pc}15` : 'transparent', color: catFilter===c ? pc : '#6b7280', fontSize:11, fontWeight:600, cursor:'pointer',
                  }}>{c==='all' ? 'Todos' : c}</button>
                ))}
              </div>
            )}

            {filteredDishes.length === 0 ? (
              <div style={{ textAlign:'center', padding:'32px 0', color:'#9ca3af' }}>
                <div style={{ fontSize:40, marginBottom:8 }}>🍽️</div>
                <p style={{ fontSize:13 }}>No hay platillos disponibles</p>
              </div>
            ) : (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                {filteredDishes.map(dish => {
                  const photo = Array.isArray(dish.photos) && dish.photos.length > 0 ? dish.photos[dish.main_photo_idx||0] : null;
                  const inOrder = order.find(i=>i.dish_id===dish.id);
                  return (
                    <div key={dish.id} onClick={() => setDetailDish(dish)} style={{ borderRadius:12, border:'1px solid #e5e7eb', overflow:'hidden', cursor:'pointer', position:'relative', background:'#fff' }}>
                      {photo
                        ? <img src={photo} alt={dish.name} style={{ width:'100%', height:90, objectFit:'cover', display:'block' }} />
                        : <div style={{ height:90, background:'#f3f4f6', display:'flex', alignItems:'center', justifyContent:'center', fontSize:32 }}>🍽️</div>}
                      {dish.is_featured && <div style={{ position:'absolute', top:6, left:6, background:'#f59e0b', borderRadius:'50%', width:18, height:18, display:'flex', alignItems:'center', justifyContent:'center' }}><Star size={10} style={{ color:'#fff', fill:'#fff' }} /></div>}
                      {inOrder && <div style={{ position:'absolute', top:6, right:6, background:pc, color:'#fff', borderRadius:'50%', width:20, height:20, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700 }}>{inOrder.qty}</div>}
                      <div style={{ padding:'8px 10px' }}>
                        <p style={{ fontWeight:600, fontSize:12, margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{dish.name}</p>
                        {dish.price != null && <p style={{ fontWeight:700, fontSize:13, color:pc, margin:'2px 0 0' }}>${Number(dish.price).toFixed(2)}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {order.length > 0 && (
              <div style={{ marginTop:14, padding:'10px 14px', borderRadius:12, background:`${pc}10`, border:`1px solid ${pc}30` }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                  <span style={{ fontSize:12, fontWeight:700, color:pc }}>🛒 {totalItems} platillo{totalItems!==1?'s':''}</span>
                  <span style={{ fontSize:13, fontWeight:800, color:pc }}>${totalPrice.toFixed(2)}</span>
                </div>
                {order.map(item => (
                  <div key={item.dish_id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:12, color:'#374151', marginBottom:2 }}>
                    <span>{item.qty}× {item.name}</span>
                    <button onClick={e=>{e.stopPropagation();removeFromOrder(item.dish_id);}} style={{ background:'none', border:'none', cursor:'pointer', color:'#9ca3af', fontSize:14, lineHeight:1 }}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {step===4 && (
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <h3 style={{ fontWeight:700, fontSize:17, margin:0 }}>{t.step4}</h3>
            {[
              { label:'Nombre', val: resType==='external' ? guestName : nickname, editStep:1 },
              { label:'Personas', val: String(partySize), editStep:2 },
              { label:'Fecha', val: selectedDate ? format(selectedDate,"EEEE d 'de' MMMM",{locale:es}) : '—', editStep:2 },
              { label:'Hora', val: selectedTime || '—', editStep:2 },
            ].map(row => (
              <div key={row.label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 14px', background:'#f9fafb', borderRadius:10 }}>
                <span style={{ color:'#6b7280', fontSize:13 }}>{row.label}</span>
                <span style={{ fontWeight:600, fontSize:13, textTransform:'capitalize' }}>{row.val}</span>
                <button onClick={() => setStep(row.editStep)} style={{ color:pc, fontSize:13, fontWeight:600, background:'none', border:'none', cursor:'pointer' }}>Editar</button>
              </div>
            ))}
            {occasion && (
              <div style={{ padding:'10px 14px', background:'#f9fafb', borderRadius:10, display:'flex', justifyContent:'space-between' }}>
                <span style={{ color:'#6b7280', fontSize:13 }}>Ocasión</span>
                <span style={{ fontWeight:600, fontSize:13 }}>{OCCASIONS.find(o=>o.id===occasion)?.label}{customOccasion ? ` · ${customOccasion}` : ''}</span>
              </div>
            )}
            {allergies && <div style={{ padding:'10px 14px', background:'#fffbeb', borderRadius:10, border:'1px solid #fcd34d' }}><p style={{ fontWeight:600, fontSize:12, color:'#92400e', margin:'0 0 2px' }}>Alergias</p><p style={{ fontSize:13, color:'#92400e', margin:0 }}>{allergies}</p></div>}
            {requests && <div style={{ padding:'10px 14px', background:'#eff6ff', borderRadius:10, border:'1px solid #bfdbfe' }}><p style={{ fontWeight:600, fontSize:12, color:'#1d4ed8', margin:'0 0 2px' }}>Petición</p><p style={{ fontSize:13, color:'#1d4ed8', margin:0 }}>{requests}</p></div>}
            {order.length > 0 && (
              <div style={{ padding:'10px 14px', background:`${pc}08`, borderRadius:10, border:`1px solid ${pc}30` }}>
                <p style={{ fontWeight:700, fontSize:13, color:pc, margin:'0 0 6px' }}>🍽️ {t.yourOrder}</p>
                {order.map(i=><div key={i.dish_id} style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:'#374151', marginBottom:2 }}><span>{i.qty}× {i.name}</span><span style={{ fontWeight:700, color:pc }}>${((i.price||0)*i.qty).toFixed(2)}</span></div>)}
                <div style={{ display:'flex', justifyContent:'space-between', fontWeight:800, fontSize:14, color:pc, borderTop:'1px solid #e5e7eb', marginTop:6, paddingTop:6 }}><span>{t.estimatedTotal}</span><span>${totalPrice.toFixed(2)}</span></div>
              </div>
            )}

            {payMethods.length > 0 && (
              <div style={{ marginTop:4 }}>
                <p style={{ fontWeight:700, fontSize:13, color:'#374151', margin:'0 0 8px' }}>{t.paymentMethod}</p>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {payMethods.includes('restaurant') && (
                    <label style={{ display:'flex', alignItems:'center', gap:10, padding:'11px 14px', border:`2px solid ${paymentSel==='restaurant'?pc:'#e5e7eb'}`, borderRadius:10, cursor:'pointer', background:paymentSel==='restaurant'?`${pc}08`:'transparent' }}>
                      <input type="radio" name="pay" checked={paymentSel==='restaurant'} onChange={()=>setPaymentSel('restaurant')} style={{ accentColor:pc }} />
                      <div><div style={{ fontWeight:600, fontSize:13 }}>🏪 {t.payAtRest}</div><div style={{ fontSize:11, color:'#9ca3af' }}>Al llegar al restaurante</div></div>
                    </label>
                  )}
                  {payMethods.includes('stripe') && (
                    <label style={{ display:'flex', alignItems:'center', gap:10, padding:'11px 14px', border:`2px solid ${paymentSel==='stripe'?pc:'#e5e7eb'}`, borderRadius:10, cursor:'pointer', background:paymentSel==='stripe'?`${pc}08`:'transparent' }}>
                      <input type="radio" name="pay" checked={paymentSel==='stripe'} onChange={()=>setPaymentSel('stripe')} style={{ accentColor:pc }} />
                      <div style={{ flex:1 }}><div style={{ fontWeight:600, fontSize:13 }}>💳 {t.payOnline}</div><div style={{ fontSize:11, color:'#9ca3af' }}>Visa, Mastercard, AMEX</div></div>
                    </label>
                  )}
                  {payMethods.includes('paypal') && (
                    <label style={{ display:'flex', alignItems:'center', gap:10, padding:'11px 14px', border:`2px solid ${paymentSel==='paypal'?pc:'#e5e7eb'}`, borderRadius:10, cursor:'pointer', background:paymentSel==='paypal'?`${pc}08`:'transparent' }}>
                      <input type="radio" name="pay" checked={paymentSel==='paypal'} onChange={()=>setPaymentSel('paypal')} style={{ accentColor:pc }} />
                      <div><div style={{ fontWeight:600, fontSize:13 }}><span style={{ color:'#003087' }}>Pay</span><span style={{ color:'#009cde' }}>Pal</span></div><div style={{ fontSize:11, color:'#9ca3af' }}>Paga con PayPal</div></div>
                    </label>
                  )}
                  {payMethods.includes('okwallet') && (
                    <label style={{ display:'flex', alignItems:'center', gap:10, padding:'11px 14px', border:`2px solid ${paymentSel==='okwallet'?pc:'#e5e7eb'}`, borderRadius:10, cursor:'pointer', background:paymentSel==='okwallet'?`${pc}08`:'transparent' }}>
                      <input type="radio" name="pay" checked={paymentSel==='okwallet'} onChange={()=>setPaymentSel('okwallet')} style={{ accentColor:pc }} />
                      <div style={{ flex:1 }}><div style={{ fontWeight:700, fontSize:13, color:pc }}>OkWallet</div><div style={{ fontSize:11, color:'#9ca3af' }}>{t.payWallet}</div></div>
                      <div style={{ fontSize:18 }}>👛</div>
                    </label>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={footStyle}>
        <div style={{ display:'flex', gap:10 }}>
          {step > 1 && (
            <button onClick={() => setStep(s=>s-1)} style={{ flex:1, padding:'14px', border:'2px solid #e5e7eb', borderRadius:12, background:'transparent', fontWeight:700, fontSize:14, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              <ChevronLeft size={18} /> Atrás
            </button>
          )}
          {step < 4 ? (
            <button onClick={() => setStep(s=>s+1)} disabled={step===1 && !canGo1} style={{ flex:1, padding:'14px', border:'none', borderRadius:12, background: (step===1 && !canGo1) ? '#d1d5db' : pc, color:'#fff', fontWeight:700, fontSize:14, cursor:(step===1 && !canGo1)?'not-allowed':'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              {step===3 ? (order.length>0 ? `${t.next} (${totalItems})` : t.next) : t.next} <ChevronRight size={18} />
            </button>
          ) : (
            <button onClick={handleConfirm} disabled={submitting} style={{ flex:1, padding:'14px', border:'none', borderRadius:12, background:submitting ? '#d1d5db' : pc, color:'#fff', fontWeight:700, fontSize:14, cursor:submitting?'not-allowed':'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
              <Send size={16} /> {submitting ? '...' : (paymentSel !== 'restaurant' ? t.pay : t.confirm)}
            </button>
          )}
        </div>
        <p style={{ fontSize:11, color:'#9ca3af', textAlign:'center', margin:'10px 0 0' }}>Powered by <strong style={{ color:'#f97316' }}>OkReserva</strong></p>
      </div>

      {detailDish && (
        <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'flex-end', zIndex:20 }} onClick={()=>setDetailDish(null)}>
          <div style={{ background:'#fff', borderRadius:'20px 20px 0 0', width:'100%', overflow:'hidden' }} onClick={e=>e.stopPropagation()}>
            {(Array.isArray(detailDish.photos) && detailDish.photos.length>0)
              ? <img src={detailDish.photos[detailDish.main_photo_idx||0]} alt="" style={{ width:'100%', height:160, objectFit:'cover', display:'block' }} />
              : <div style={{ height:80, background:'#f3f4f6', display:'flex', alignItems:'center', justifyContent:'center', fontSize:40 }}>🍽️</div>}
            <div style={{ padding:16 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 }}>
                <h3 style={{ fontWeight:800, fontSize:16, margin:0 }}>{detailDish.name}</h3>
                <button onClick={()=>setDetailDish(null)} style={{ background:'none', border:'none', cursor:'pointer', color:'#9ca3af', fontSize:22 }}>×</button>
              </div>
              {detailDish.description && <p style={{ fontSize:13, color:'#9ca3af', margin:'0 0 8px' }}>{detailDish.description}</p>}
              {detailDish.price != null && <p style={{ fontWeight:800, fontSize:20, color:pc, margin:'0 0 14px' }}>${Number(detailDish.price).toFixed(2)}</p>}
              <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                <div style={{ display:'flex', border:'2px solid #e5e7eb', borderRadius:10, overflow:'hidden' }}>
                  <button onClick={()=>setDishQty(p=>({...p,[detailDish.id]:Math.max(0,(p[detailDish.id]||0)-1)}))} style={{ padding:'8px 14px', background:'transparent', border:'none', cursor:'pointer', fontWeight:700, fontSize:18, color:'#374151' }}>−</button>
                  <span style={{ padding:'8px 12px', fontWeight:800, fontSize:16, minWidth:32, textAlign:'center', color:'#374151' }}>{dishQty[detailDish.id]||0}</span>
                  <button onClick={()=>setDishQty(p=>({...p,[detailDish.id]:(p[detailDish.id]||0)+1}))} style={{ padding:'8px 14px', background:'transparent', border:'none', cursor:'pointer', fontWeight:700, fontSize:18, color:'#374151' }}>+</button>
                </div>
                <button disabled={!(dishQty[detailDish.id]>0)} onClick={()=>addToOrder(detailDish,dishQty[detailDish.id]||1)} style={{ flex:1, padding:'12px', border:'none', borderRadius:10, background: (dishQty[detailDish.id]>0) ? pc : '#e5e7eb', color: (dishQty[detailDish.id]>0) ? '#fff' : '#9ca3af', fontWeight:700, fontSize:13, cursor:(dishQty[detailDish.id]>0)?'pointer':'not-allowed' }}>
                  {dishQty[detailDish.id]>0 ? `Agregar ${dishQty[detailDish.id]} — $${((detailDish.price||0)*(dishQty[detailDish.id]||0)).toFixed(2)}` : 'Selecciona cantidad'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCal && (
        <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:30, padding:16 }} onClick={()=>setShowCal(false)}>
          <div style={{ background:'#fff', borderRadius:24, padding:28, width:'100%', maxWidth:360 }} onClick={e=>e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <button onClick={()=>setCalMonth(new Date(calMonth.getFullYear(),calMonth.getMonth()-1))} style={{ border:'none', background:'transparent', cursor:'pointer', padding:8 }}><ChevronLeft size={20} /></button>
              <h4 style={{ fontWeight:800, fontSize:16, margin:0, textTransform:'capitalize' }}>{format(calMonth,'MMMM yyyy',{locale:es})}</h4>
              <button onClick={()=>setCalMonth(new Date(calMonth.getFullYear(),calMonth.getMonth()+1))} style={{ border:'none', background:'transparent', cursor:'pointer', padding:8 }}><ChevronRight size={20} /></button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:4, marginBottom:8 }}>
              {['D','L','M','M','J','V','S'].map((d,i)=><div key={i} style={{ textAlign:'center', fontSize:12, fontWeight:700, color:'#9ca3af', padding:4 }}>{d}</div>)}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:4 }}>{renderCal()}</div>
            <div style={{ display:'flex', gap:10, marginTop:20 }}>
              <button onClick={()=>setShowCal(false)} style={{ flex:1, padding:'14px', background:'#f3f4f6', border:'none', borderRadius:16, fontWeight:700, fontSize:15, cursor:'pointer' }}>Cancelar</button>
              <button onClick={()=>setShowCal(false)} disabled={!selectedDate} style={{ flex:1, padding:'14px', background:selectedDate?pc:'#e5e7eb', border:'none', borderRadius:16, fontWeight:700, fontSize:15, cursor:selectedDate?'pointer':'not-allowed', color:'#fff' }}>Confirmar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
